import { UsersRound } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

/**
 * Staff pick the organization the batch belongs to. Its own component so the organizations catalog is only
 * requested when it renders — members never see it and must not trigger the call.
 */
export function OrganizationField() {
  const { t } = useTranslations("payoutBatches");
  const catalog = useOrganizationsCatalog();
  const { watch, setValue, resetField, formState } = useFormContext();
  const value = (watch("organizationId") as string | undefined) || null;
  const errorMessage = formState.errors.organizationId?.message;
  // Only organizations in the preloaded catalog are known; one reached through search shows no notice.
  const membersLocked =
    !!value && catalog.organizations.find((org) => org.id === value)?.payout_batches_enabled === false;

  return (
    <div className="space-y-1.5">
      <OrganizationScopePicker
        id="payout-batch-organization"
        mode="required"
        value={value}
        onValueChange={(organizationId) => {
          setValue("organizationId", organizationId ?? "", {
            shouldDirty: true,
            shouldValidate: true,
          });
          // The wallets and the corridor availability belong to the organization; a new one starts clean.
          resetField("sourceCurrency");
          resetField("corridor");
        }}
        catalog={catalog}
        labels={{
          label: t("org_picker_label"),
          placeholder: t("org_picker_placeholder"),
          loading: t("org_picker_loading"),
          empty: t("org_picker_empty"),
          searching: t("org_picker_searching"),
          searchError: t("org_picker_search_error"),
          catalogError: t("org_picker_catalog_error"),
          catalogTruncated: t("org_picker_catalog_truncated"),
          requiredHint: t("org_picker_required_hint"),
          orgHint: t("org_picker_org_hint"),
        }}
      />
      {typeof errorMessage === "string" && errorMessage && (
        <p className="text-xs text-destructive">{errorMessage}</p>
      )}
      {membersLocked && (
        <ColocaAlert
          variant="secondary"
          icon={UsersRound}
          heading={t("setup_org_not_enabled_title")}
          text={t("setup_org_not_enabled_text")}
        />
      )}
    </div>
  );
}
